import { useState } from 'react';
import { AlertTriangle, CheckCircle, Wrench, XCircle, ChevronDown, ChevronRight } from 'lucide-react';

interface Violation {
  type: string;
  severity: 'error' | 'warning';
  message: string;
  location?: string;
}

interface RepairInfo {
  wasRepaired: boolean;
  originalPlan?: unknown;
  repairedPlan?: unknown;
  repairReason?: string;
  violationsFixed?: number;
}

interface ConsistencyResult {
  passed: boolean;
  violations: Violation[];
  checkedAt?: string;
}

interface RepairViolationsPanelProps {
  repairInfo: RepairInfo | null;
  consistencyResult: ConsistencyResult | null;
  planCheck: unknown | null;
  planGate: {
    triggered: boolean;
    source: 'heuristic' | 'check' | 'none';
    reasons: string[];
  } | null;
  mediumWarnings?: Array<{
    type: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    message: string;
    fix?: string;
    fix_hint?: string;
  }> | null;
}

function extractPlanData(plan: unknown): { synopsis?: string; beats?: unknown[]; mood?: unknown } | null {
  if (!plan || typeof plan !== 'object') return null;
  const p = plan as Record<string, unknown>;
  const data = p.data as Record<string, unknown> | undefined;
  if (!data) return null;
  return {
    synopsis: typeof data.synopsis === 'string' ? data.synopsis : undefined,
    beats: Array.isArray(data.beats) ? data.beats : undefined,
    mood: data.mood,
  };
}

export function RepairViolationsPanel({
  repairInfo,
  consistencyResult,
  planCheck,
  planGate,
  mediumWarnings,
}: RepairViolationsPanelProps) {
  const [showOriginalPlan, setShowOriginalPlan] = useState(false);
  const [showRepairedPlan, setShowRepairedPlan] = useState(false);
  const [showPlanCheck, setShowPlanCheck] = useState(false);
  const [showPlanGate, setShowPlanGate] = useState(false);

  const hasRepair = repairInfo?.wasRepaired;
  const hasPassed = consistencyResult?.passed ?? true;
  const violations = consistencyResult?.violations ?? [];

  const originalPlanData = extractPlanData(repairInfo?.originalPlan);
  const repairedPlanData = extractPlanData(repairInfo?.repairedPlan);
  const planCheckData =
    planCheck && typeof planCheck === 'object'
      ? ((planCheck as Record<string, unknown>).data ?? planCheck)
      : planCheck;
  const hasPlanCheck = Boolean(planCheckData);

  return (
    <div className="space-y-4">
      {/* Repair Status */}
      {repairInfo && (
        <div
          className={`p-4 rounded-lg border ${
            hasRepair
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-success/10 border-success/30'
          }`}
        >
          <div className="flex items-center gap-2">
            {hasRepair ? (
              <>
                <Wrench size={16} className="text-amber-400" />
                <span className="text-amber-400 font-medium">Plan Required Repair</span>
              </>
            ) : (
              <>
                <CheckCircle size={16} className="text-success" />
                <span className="text-success font-medium">Plan Passed Validation</span>
              </>
            )}
          </div>
          {hasRepair && repairInfo.repairReason && (
            <p className="text-sm text-mist mt-2">{repairInfo.repairReason}</p>
          )}
          {hasRepair && repairInfo.violationsFixed !== undefined && (
            <p className="text-xs text-mist/70 mt-1">
              {repairInfo.violationsFixed} violation{repairInfo.violationsFixed !== 1 ? 's' : ''}{' '}
              fixed
            </p>
          )}
          {hasRepair && hasPlanCheck && (
            <div className="mt-3">
              <button
                onClick={() => setShowPlanCheck(!showPlanCheck)}
                className="flex items-center gap-1 text-xs text-mist hover:text-parchment"
              >
                {showPlanCheck ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                View Plan Check JSON
              </button>
              {showPlanCheck && (
                <pre className="mt-2 p-3 bg-void/70 rounded-lg text-xs text-mist overflow-auto max-h-48">
                  {JSON.stringify(planCheckData, null, 2)}
                </pre>
              )}
            </div>
          )}
          {planGate && (
            <div className="mt-3">
              <button
                onClick={() => setShowPlanGate(!showPlanGate)}
                className="flex items-center gap-1 text-xs text-mist hover:text-parchment"
              >
                {showPlanGate ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                View Plan Gate Details
              </button>
              {showPlanGate && (
                <pre className="mt-2 p-3 bg-void/70 rounded-lg text-xs text-mist overflow-auto max-h-48">
                  {JSON.stringify(planGate, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}

      {/* Plan Comparison (when repair happened) */}
      {hasRepair && !!(originalPlanData || repairedPlanData) && (
        <div className="space-y-3">
          {/* Synopsis Comparison */}
          {(originalPlanData?.synopsis || repairedPlanData?.synopsis) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {originalPlanData?.synopsis && (
                <div className="p-3 bg-void/50 rounded-lg border border-stone/20">
                  <div className="text-xs text-mist/70 mb-1 flex items-center gap-1">
                    <XCircle size={10} className="text-ember/70" />
                    Original Synopsis
                  </div>
                  <p className="text-sm text-mist">{originalPlanData.synopsis}</p>
                </div>
              )}
              {repairedPlanData?.synopsis && (
                <div className="p-3 bg-void/50 rounded-lg border border-success/20">
                  <div className="text-xs text-mist/70 mb-1 flex items-center gap-1">
                    <CheckCircle size={10} className="text-success/70" />
                    Repaired Synopsis
                  </div>
                  <p className="text-sm text-parchment">{repairedPlanData.synopsis}</p>
                </div>
              )}
            </div>
          )}

          {/* Expandable Plan Details */}
          {!!repairInfo.originalPlan && (
            <div>
              <button
                onClick={() => setShowOriginalPlan(!showOriginalPlan)}
                className="flex items-center gap-1 text-xs text-mist hover:text-parchment"
              >
                {showOriginalPlan ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                View Original Plan JSON
              </button>
              {showOriginalPlan && (
                <pre className="mt-2 p-3 bg-void/70 rounded-lg text-xs text-mist overflow-auto max-h-48">
                  {JSON.stringify(repairInfo.originalPlan, null, 2)}
                </pre>
              )}
            </div>
          )}
          {!!repairInfo.repairedPlan && (
            <div>
              <button
                onClick={() => setShowRepairedPlan(!showRepairedPlan)}
                className="flex items-center gap-1 text-xs text-mist hover:text-parchment"
              >
                {showRepairedPlan ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                View Repaired Plan JSON
              </button>
              {showRepairedPlan && (
                <pre className="mt-2 p-3 bg-void/70 rounded-lg text-xs text-mist overflow-auto max-h-48">
                  {JSON.stringify(repairInfo.repairedPlan, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}

      {/* Consistency Check Status */}
      {consistencyResult && (
        <div
          className={`p-4 rounded-lg border ${
            hasPassed ? 'bg-success/10 border-success/30' : 'bg-ember/10 border-ember/30'
          }`}
        >
          <div className="flex items-center gap-2">
            {hasPassed ? (
              <>
                <CheckCircle size={16} className="text-success" />
                <span className="text-success font-medium">Consistency Check Passed</span>
              </>
            ) : (
              <>
                <XCircle size={16} className="text-ember" />
                <span className="text-ember font-medium">Consistency Issues Detected</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Violations List */}
      {violations.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-parchment flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-400" />
            Violations ({violations.length})
          </h4>
          <div className="space-y-2">
            {violations.map((violation, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border text-sm ${
                  violation.severity === 'error'
                    ? 'bg-ember/10 border-ember/30'
                    : 'bg-amber-500/10 border-amber-500/30'
                }`}
              >
                <div className="flex items-start gap-2">
                  {violation.severity === 'error' ? (
                    <XCircle size={14} className="text-ember mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <div
                      className={`font-medium ${
                        violation.severity === 'error' ? 'text-ember' : 'text-amber-400'
                      }`}
                    >
                      {violation.type}
                    </div>
                    <div className="text-mist mt-0.5">{violation.message}</div>
                    {violation.location && (
                      <div className="text-mist/70 text-xs mt-1 font-mono">{violation.location}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!repairInfo && !consistencyResult && (
        <div className="text-center py-8 text-mist text-sm">
          No repair or consistency data available
        </div>
      )}

      {mediumWarnings && mediumWarnings.length > 0 && (
        <div className="p-4 rounded-lg border border-amber-500/40 bg-amber-500/5 space-y-2">
          <div className="flex items-center gap-2 text-xs text-amber-300">
            <AlertTriangle size={14} />
            Logged medium severity consistency warnings (do not block generation)
          </div>
          <div className="space-y-2 text-xs text-mist">
            {mediumWarnings.map((warning, index) => (
              <div key={`${warning.type}-${index}`} className="space-y-1">
                <div className="text-parchment text-[11px] font-semibold">
                  {warning.type} · {warning.severity}
                </div>
                <p>{warning.message}</p>
                {warning.fix && (
                  <p className="text-success/80 text-[11px]">
                    Fix: {warning.fix}
                  </p>
                )}
                {warning.fix_hint && (
                  <p className="text-mist/70 text-[11px]">Hint: {warning.fix_hint}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
